// Reference: javascript_object_storage blueprint
import { useState } from "react";
import type { ReactNode } from "react";
import Uppy from "@uppy/core";
import { DashboardModal } from "@uppy/react";
import XHRUpload from "@uppy/xhr-upload";
import type { UploadResult } from "@uppy/core";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface ObjectUploaderProps {
  maxNumberOfFiles?: number;
  maxFileSize?: number;
  onComplete?: (
    result: UploadResult<Record<string, unknown>, Record<string, unknown>>
  ) => void;
  buttonClassName?: string;
  children: ReactNode;
}

export function ObjectUploader({
  maxNumberOfFiles = 10,
  maxFileSize = 10485760, // 10MB default
  onComplete,
  buttonClassName,
  children,
}: ObjectUploaderProps) {
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();
  const [uppy] = useState(() =>
    new Uppy({
      debug: true,
      restrictions: {
        maxNumberOfFiles,
        maxFileSize,
        allowedFileTypes: ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx'],
      },
      autoProceed: false,
    })
      .use(XHRUpload, {
        endpoint: '/api/objects/upload/proxy',
        method: 'POST',
        formData: false, // send raw file bytes as request body
        limit: 3,
        getResponseData: (xhr: XMLHttpRequest) => {
          try {
            return JSON.parse(xhr.responseText);
          } catch {
            return {} as any;
          }
        },
        headers: (file: any) => ({
          'Content-Type': (file as any)?.type || 'application/octet-stream',
          'X-File-Name': encodeURIComponent((file as any)?.name || '')
        }),
      })
      // Helpful diagnostics for failures (e.g., CORS or signature mismatch)
      .on('upload-error', (file, error, response) => {
        console.error('[Uploader] upload-error for', file?.name, '->', error, response);
        try {
          const status = response && (response.status || (response && (response as any).statusCode));
          if (status === 501) {
            toast({ title: 'File uploads disabled', description: 'File uploads are disabled in this environment for the demo.' });
            return;
          }
        } catch (e) {
          // ignore
        }
        toast({ title: 'Upload failed', description: `Failed to upload ${file?.name || 'file'}.` });
      })
      .on('error', (error) => {
        console.error('[Uploader] error:', error);
      })
      .on('restriction-failed', (file, error) => {
        console.warn('[Uploader] restriction-failed for', file?.name, '->', error?.message || error);
      })
      .on("complete", (result) => {
        onComplete?.(result);
        setShowModal(false);
      })
  );

  return (
    <div>
      <Button onClick={() => setShowModal(true)} className={buttonClassName} type="button">
        {children}
      </Button>

      <DashboardModal
        uppy={uppy}
        open={showModal}
        onRequestClose={() => setShowModal(false)}
        proudlyDisplayPoweredByUppy={false}
      />
    </div>
  );
}
